import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { FIRM, PRACTICE_AREAS } from "@/data/site";
import { ArrowUpRight, Instagram, Linkedin, Twitter } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Subscribed. Thank you.");
      setEmail("");
    } catch (err) {
      const msg = err?.response?.status === 409 ? "You are already subscribed." : "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative bg-navy-deep text-ivory pt-24 pb-10 overflow-hidden" data-testid="site-footer">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 pb-16 border-b border-white/10">
          <div className="md:col-span-5 space-y-8">
            <div>
              <div className="mono text-xs uppercase tracking-[0.3em] text-gold mb-4">Newsletter · N°01</div>
              <h3 className="serif text-4xl md:text-5xl leading-[1.02]">Judgments & counsel, quarterly.</h3>
            </div>
            <form onSubmit={submit} className="flex items-center gap-3 max-w-md border-b border-white/25" data-testid="footer-newsletter-form">
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 bg-transparent py-3 text-sm placeholder:text-ivory/40 focus:outline-none"
                data-testid="newsletter-email"
              />
              <button type="submit" disabled={loading} className="text-sm uppercase tracking-[0.22em] text-gold flex items-center gap-2" data-testid="newsletter-submit">
                {loading ? "Sending" : "Subscribe"} <ArrowUpRight className="h-4 w-4" />
              </button>
            </form>
            <p className="text-xs text-ivory/50 max-w-sm">No spam. Only case-law digests and firm notes. Unsubscribe anytime.</p>
          </div>

          <div className="md:col-span-3 space-y-4">
            <div className="mono text-xs uppercase tracking-[0.3em] text-ivory/50">Firm</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="editorial-link">About</Link></li>
              <li><Link to="/practice-areas" className="editorial-link">Practice Areas</Link></li>
              <li><Link to="/blog" className="editorial-link">Journal</Link></li>
              <li><Link to="/faq" className="editorial-link">FAQ</Link></li>
              <li><Link to="/contact" className="editorial-link">Contact</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4 space-y-4">
            <div className="mono text-xs uppercase tracking-[0.3em] text-ivory/50">Chambers</div>
            <address className="not-italic text-sm leading-relaxed text-ivory/80">
              {FIRM.address.line1}<br />
              {FIRM.address.line2}<br />
              {FIRM.address.city} – {FIRM.address.pin}, {FIRM.address.country}
            </address>
            <div className="space-y-1 text-sm">
              <div><a href={`tel:${FIRM.phoneRaw}`} className="editorial-link" data-testid="footer-phone">{FIRM.phone}</a></div>
              <div><a href={`mailto:${FIRM.email}`} className="editorial-link" data-testid="footer-email">{FIRM.email}</a></div>
              <p className="text-ivory/60">{FIRM.hours}</p>
            </div>
          </div>
        </div>

        {/* Practice mini-grid */}
        <div className="grid grid-cols-2 md:grid-cols-7 gap-6 py-10 border-b border-white/10">
          {PRACTICE_AREAS.map(p => (
            <Link key={p.slug} to={`/practice-areas/${p.slug}`} className="group">
              <div className="text-sm group-hover:text-gold transition-colors">{p.title}</div>
            </Link>
          ))}
        </div>

        <div className="py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4 mono text-[11px] uppercase tracking-[0.28em] text-ivory/50">
            <span>© {new Date().getFullYear()} Helkar & Associates</span>
            <span className="text-gold">·</span>
            <Link to="/privacy" className="editorial-link">Privacy</Link>
            <Link to="/terms" className="editorial-link">Terms</Link>
            <Link to="/disclaimer" className="editorial-link">Disclaimer</Link>
          </div>
          <div className="flex items-center gap-4 text-ivory/70">
            <a href="#" aria-label="LinkedIn"><Linkedin className="h-4 w-4" /></a>
            <a href="#" aria-label="Instagram"><Instagram className="h-4 w-4" /></a>
            <a href="#" aria-label="Twitter"><Twitter className="h-4 w-4" /></a>
          </div>
        </div>

        <div className="text-[7.2vw] serif italic leading-none text-ivory/5 select-none pointer-events-none -mb-6 tracking-tight whitespace-nowrap text-center">
          Helkar & Associates
        </div>
      </div>
    </footer>
  );
}
